import * as React from "react";
import ReactDOM from "react-dom";
import { createRoot } from "react-dom/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import DataTable from "./DataTable";
import { incidentOverview } from "@services/api/commonApis";
import { useQuery } from "react-query";
import DialogLoader from "../loaders/DialogLoader";
import {
  BrainCogIcon,
  Download,
  MicIcon,
  PlayCircle,
  SearchIcon,
  Triangle,
} from "lucide-react";
import { AssetEntityInspectorContent } from "@pages/homepage/asset-auto-discovery/AssetEntityInspector";
import TextComponent from "./Textcomponent";
import { Input } from "../ui/input";
import ShadowGPT from "./ShadowGPT";
import { IncidentOverviewProps } from "./CommonTypes";
import { EpochToDateTime, getShadow360URL } from "@utils/utils";
import ShadowGPTFloat from "./ShadowGPTFloat";
import UserInvestigator from "./UserInvestigator";
import EventDropdown from "@pages/homepage/aml-anomaly-detection/EventDropdown";
import { isFeatureAllowed, ALL_KEYS } from "@utils/licenseUtils";
import { marked } from "marked";

const IncidentOverviewComponent: React.FC<IncidentOverviewProps> = ({
  openState,
  incidentProps,
  titleRef,
}) => {
  const [eventSearch, setEventSearch] = React.useState<string>("");
  const [recording, setRecording] = React.useState<boolean>(false);
  const [shadowOpen, setShadowOpen] = React.useState<boolean>(false);
  const [userDetailId, setUserDetailId] = React.useState<string>("");
  const shadowRespRef = React.useRef(null);
  let passData: any = {};

  if (incidentProps && Object.keys(incidentProps).length > 0) {
    const eventid: any = [];
    if (incidentProps["eventNames"]) {
      Object.keys(incidentProps.eventNames).map((key: string) => {
        eventid.push(key);
      });
    }
    passData = {
      gid: incidentProps.globalID,
      // events_id: eventid.join(","),
      events: incidentProps.data?.events || incidentProps.data || [],
      sensor: incidentProps.sensor,
      time_stamp: incidentProps.time_seen,
      anomalous_ip_mac: incidentProps.anomalous_ip_mac,
      anomalous_ip: incidentProps.anomalous_ip,
      anomaly_id: incidentProps.anomaly_id,
      username: incidentProps.username,
    };
    if (incidentProps?.anomaly_type == "entity") {
      delete passData["username"];
    }
  }

  const {
    data: incidentData,
    isError: _dataError,
    refetch: incidentRefetch,
    isFetching: _dataFetching,
  } = useQuery(
    ["incidentOverview", incidentProps.globalID],
    ({ signal }) => incidentOverview(passData, signal),
    {
      enabled: false,
    },
  );

  const [incidentDetail, setIncidentDetail] = React.useState<any>([]);
  const [anomalyData, setAnomalyData] = React.useState<any>({});
  const [mitre_attack, setMitreAttack] = React.useState<any>([]);
  const [mitre_defend, setMitreDefend] = React.useState<any>([]);
  const [assetDetailOpen, setAssetDetailOpen] = React.useState<boolean>(false);
  const [loggedUsers, setLoggedUsers] = React.useState<any>([]);
  const [loggedUsersModalOpen, setLoggedUsersModalOpen] =
    React.useState<boolean>(false);
  // const [_amlDesc, setAmlDesc] = React.useState<string>("");
  const [visibleCols, setVisibleCols] = React.useState<Record<string, boolean>>(
    {
      Time: true,
      "Source IP": true,
      "Source Port": true,
      "Destination IP": true,
      "Destination Port": true,
      "Destination MAC": false,
      Sensor: false,
      Version: false,
      "User ID": false,
    },
  );

  React.useEffect(() => {
    if (openState && incidentProps && Object.keys(incidentProps).length > 0) {
      incidentRefetch();
    }
  }, [openState]);

  const entityLogcols = [
    { header: "Time", accessor: "Time" },
    { header: "Source IP", accessor: "Source IP" },
    { header: "Source Port", accessor: "Source Port" },
    { header: "Destination IP", accessor: "Destination IP" },
    { header: "Destination Port", accessor: "Destination Port" },
    { header: "Destination MAC", accessor: "Destination MAC" },
    { header: "Sensor", accessor: "Sensor" },
    { header: "Version", accessor: "Version" },
    { header: "User ID", accessor: "User ID" },
    { header: "Fields", accessor: "fields", type: "component" },
  ];

  const userLogcols = [
    { header: "Time", accessor: "Time" },
    { header: "User ID", accessor: "User ID", type: "component" },
    { header: "Workstation IP", accessor: "Workstation IP" },
    { header: "Workstation Name", accessor: "Workstation Name" },
    { header: "Log Source", accessor: "log_source" },
    { header: "Fields", accessor: "fields", type: "component" },
  ];

  const logcols =
    incidentProps?.anomaly_type == "entity" ? entityLogcols : userLogcols;

  // let score: number = 0;

  React.useEffect(() => {
    if (incidentData) {
      const anomalyDetail = incidentData.anomaly_info;
      const incData = incidentData.data;
      setMitreAttack(incidentData.mitre_attack);
      setMitreDefend(incidentData.mitre_defend);
      setLoggedUsers((anomalyDetail as any)?.login_users || []);
      let desc =
        "rule_desc_with_variable" in anomalyDetail
          ? anomalyDetail.rule_desc_with_variable
          : "";
      if (desc) {
        if (desc.trim() == "") {
          desc =
            "description" in anomalyDetail ? anomalyDetail.description : "";
        } else {
          for (const [key, value] of Object.entries(incidentProps)) {
            desc = desc.replace(
              `<<${key}>>`,
              `<span style='color:orange'>${value}</span>`,
            );
          }
          desc = "<span class='font-bold'>Description: </span>" + desc;
        }
      }

      desc =
        "<span class='font-bold'>Description: </span>" +
        anomalyDetail.description;

      if (
        "possible_root_causes" in anomalyDetail &&
        anomalyDetail.possible_root_causes != ""
      ) {
        anomalyDetail["triggers"] = anomalyDetail.possible_root_causes;
      }

      setAnomalyData(anomalyDetail);
      setIncidentDetail(incData);
      // setAmlDesc(desc);

      if (incidentProps?.anomaly_type == "user") {
        visibleCols["User ID"] = true;
        visibleCols["Source Port"] = false;
        visibleCols["Destination Port"] = false;
        visibleCols["Destination IP"] = false;
        setVisibleCols({ ...visibleCols });
      }

      if (incData.length > 0) {
        setEventTab(incData[0].EventId);
      }
    }
  }, [incidentData]);

  interface LoggedUserRow {
    username?: string;
    mac?: string;
    host?: string;
    ip?: string;
    first_login?: string;
    last_login?: string;
    last_login_epoch?: number;
    count?: number;
  }

  const loggedUsersRows = React.useMemo<LoggedUserRow[]>(() => {
    if (!Array.isArray(loggedUsers)) return [];
    return loggedUsers.filter(
      (entry: any) => entry && typeof entry === "object",
    );
  }, [loggedUsers]);

  const loggedUsersSummary = React.useMemo(() => {
    if (loggedUsersRows.length === 0) return "";
    return loggedUsersRows[0].username || "";
  }, [loggedUsersRows]);

  const loggedUsersTableCols = [
    { header: "Username", accessor: "username" },
    { header: "First Login", accessor: "first_login" },
    { header: "Last Login", accessor: "last_login" },
    { header: "Count", accessor: "count" },
  ];

  const [mainTab, setMainTab] = React.useState<string>("detail");

  function writeWelcomeText() {
    const message = ["Welcome to ShadowGPT!", "How can I help you?"];
    const welcome = document.getElementById("welcomeMessage");
    if (welcome) {
      welcome.innerHTML = "";
      let extraDelay = 0;
      for (let d = 0; d < message.length; d++) {
        const element = document.createElement("div");
        element.classList.add("mb-1");
        welcome.appendChild(element);
        const text = message[d];
        for (let i = 0; i < text.length; i++) {
          setTimeout(
            function () {
              element.innerHTML = element.innerHTML + text[i];
            },
            20 * i + extraDelay,
          );
        }

        extraDelay += text.length * 20;
      }
    }
  }

  function openShadowGPT() {
    if (!shadowOpen) {
      const welcome = document.getElementById("welcomeMessage");
      if (welcome) {
        welcome.innerHTML = "";
      }
      setTimeout(writeWelcomeText, 500);
    }
    setShadowOpen(!shadowOpen);
  }

  const [eventTab, setEventTab] = React.useState<string>("ev1");

  function searchEvent(e: string) {
    setEventSearch(e);
  }

  const logTypes = ["entity_anomalies", "linkflow", "entity_alerts"];
  incidentProps?.eventData?.forEach((e: any) => {
    const evData = JSON.parse(e?.EventData);
    const logsrc = evData.log_source[0];
    if (!logTypes.includes(logsrc)) {
      logTypes.push(logsrc);
    }
  });

  if (incidentProps?.anomaly_type == "user") {
    logTypes.push("user_anomalies");
    logTypes.push("user_alerts");
  }

  const shadow360Url = getShadow360URL({
    anomalyId: incidentProps.anomalyId,
    startTime: incidentProps.time_seen,
    endTime: incidentProps.time_seen,
    logTypes: logTypes,
    // logTypes: ["linkflow", "entity_anomalies"],
    searchValue:
      incidentProps?.anomaly_type == "entity"
        ? incidentProps?.anomalous_ip
        : incidentProps.username,
    searchType: incidentProps?.anomaly_type == "entity" ? "ip" : "username",
    sensor: incidentProps.sensor,
  });

  function downloadReport() {
    const url = `/incidentOverviewDownload/?globalId=${incidentProps.globalID}&anomalyId=${incidentProps.anomaly_id}&anomalyTime=${incidentProps.time_seen}&action=download&s360=${btoa(shadow360Url)}&anomalyType=${incidentProps.anomaly_type}&tableHeader=Time,Source%20IP,Source%20Port,Destination%20IP,Destination%20Port,Event&timeFrame=2month&version=new&anomalous_ip_mac=${incidentProps.anomalous_ip_mac}&anomalous_ip=${incidentProps.anomalous_ip}&username=${incidentProps.username}&events=${JSON.stringify(incidentProps.data?.events || [])}&sensor=${incidentProps.sensor}`;
    window.open(url, "_blank");
  }

  let writer: any = 0;
  let writerIndex: number = 0;
  let newDescContent: string = "";
  function typeEffect(text: string) {
    const comp: any = shadowRespRef.current;
    if (comp) {
      writer = setInterval(() => {
        const nextLetter = text[writerIndex];
        if (nextLetter) {
          newDescContent += nextLetter;
          comp.innerHTML = marked.parse(newDescContent) as string;
        } else {
          clearInterval(writer);
        }
        writerIndex += 1;
      }, 5);
    }
  }

  const generalDesc =
    anomalyData && "description" in anomalyData
      ? anomalyData["description"]
      : "";
  let generatedDesc = false;
  if (incidentData && "generated_description" in incidentData) {
    const de = JSON.parse(incidentData.generated_description);
    if (de && de.length > 0) {
      typeEffect(de[0].event_description);
      generatedDesc = true;
      // generatedDesc = marked.parse(de[0].event_description) as string;
    }
  }

  React.useEffect(() => {
    if (titleRef && titleRef.current) {
      const container = document.createElement("span");
      titleRef.current.appendChild(container);
      const root = createRoot(container);

      root.render(
        <Download
          className="!w-8 !h-10 cursor-pointer inline-block ms-6 rounded-sm bg-secondary p-2 hover:bg-primary"
          onClick={() => downloadReport()}
        />,
      );

      return () => {
        root.unmount();
        titleRef.current?.removeChild(container);
      };
    }
  }, [titleRef]);

  return (
    <>
      {incidentProps &&
      incidentProps.anomalous_ip_mac != "" &&
      incidentProps.anomaly_type == "entity" &&
      assetDetailOpen ? (
        <AssetEntityInspectorContent
          mac={incidentProps.anomalous_ip_mac}
          open={assetDetailOpen}
          onClose={() => setAssetDetailOpen(false)}
        />
      ) : (
        ""
      )}

      {/* {incidentProps &&
      incidentProps.anomaly_type == "user" &&
      incidentProps.username != "" &&
      userDetailId != "" &&
      isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_ST) ? (
        <UserInvestigator
          account={userDetailId}
          openState={true}
          onClose={() => setUserDetailId("")}
        />
      ) : (
        ""
      )} */}

      {incidentProps &&
      incidentProps.anomaly_type == "user" &&
      incidentProps.username != "" &&
      userDetailId != "" &&
      isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_ACCESS) ? (
        <UserInvestigator
          account={userDetailId}
          openState={true}
          onClose={() => setUserDetailId("")}
        />
      ) : (
        ""
      )}

      <div>
        <Dialog
          open={loggedUsersModalOpen}
          onOpenChange={setLoggedUsersModalOpen}
        >
          <DialogContent className="max-w-[70vw] max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Logged Users</DialogTitle>
            </DialogHeader>
            <div className="h-full overflow-auto">
              <DataTable
                cols={loggedUsersTableCols as any}
                data={loggedUsersRows as any}
                rowsPerPage={10}
                pagination={true}
                showPagination={true}
                totalCount={loggedUsersRows.length}
                isSearchVisibleCols={true}
              />
            </div>
          </DialogContent>
        </Dialog>

        <DialogLoader show={_dataFetching && !_dataError} />
        <ShadowGPTFloat
          data={{
            anomalous_hostname: incidentProps.anomalous_hostname,
            anomalous_ip: incidentProps.anomalous_ip,
            anomalous_mac: incidentProps.anomalous_ip_mac,
            anomaly_id: incidentProps.anomaly_id,
            anomaly_type: incidentProps.anomaly_type,
            anomaly_time: incidentProps.Time,
            epoch_time: parseInt(incidentProps.inserted_time),
            globalID: incidentProps.globalID,
            score: "score" in incidentProps ? incidentProps.score : 0,
            name: "name" in anomalyData ? anomalyData.name : "",
            page: "incidentOverview",
            sourceIp: incidentData?.data?.[0]?.EventData?.sip,
            sourceMac: incidentData?.data?.[0]?.EventData?.smac,
            destinationIp: incidentData?.data?.[0]?.EventData?.dip,
            // eventData: incidentData?.data?.[0]?.EventData,
            // events: incidentProps?.data?.events,
          }}
          displayIn="dialog"
        />
        {!titleRef && (
          <>
            <span className="text-lg font-semibold">Anomaly Overview</span>{" "}
            <Download
              className="!w-8 !h-10 cursor-pointer inline-block ms-6 rounded-sm bg-secondary p-2 hover:bg-primary"
              onClick={() => downloadReport()}
            />
          </>
        )}
        <div className="z-10 hidden">
          <div
            onClick={() => setMainTab("detail")}
            className={
              "text-sm p-3 rounded-t-md inline-block cursor-pointer " +
              (mainTab == "detail" ? "bg-primary-background font-bold" : "")
            }
          >
            <SearchIcon className="inline-block me-2" size={14} />
            <span>Incident Details</span>
          </div>
          <div
            onClick={() => setMainTab("shadow")}
            className={
              "text-sm p-3 rounded-t-md inline-block cursor-pointer " +
              (mainTab == "shadow" ? "bg-primary-background font-bold" : "")
            }
          >
            <BrainCogIcon className="inline-block me-2" size={14} />
            <span>Shadow GPT</span>
          </div>
        </div>
        <div className="relative">
          <div className="grid gap-4 grid-cols-12 border-b">
            <div className="col-span-6">
              {anomalyData && "name" in anomalyData ? (
                <div className="text-lg underline decoration-red-500 decoration-2 underline-offset-8">
                  {anomalyData && "name" in anomalyData ? (
                    anomalyData["name"]
                  ) : (
                    <div className="text-primary">Name not found</div>
                  )}
                </div>
              ) : null}

              {generatedDesc ? (
                <div className="text-sm mt-3 mb-5 border-l-2 rounded-l-md border-primary ps-2 py-1">
                  <div ref={shadowRespRef}>{generatedDesc}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <img
                      className="w-6 h-6 object-contain rounded-full shadowgpt-image"
                      src={
                        process.env.STATIC_PATH + "/img/shadowGPT/Circle.gif"
                      }
                    />
                    <div className="opacity-50 text-[10px] underline underline-offset-2 decoration-primary">
                      Generated by ShadowGPT
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm mt-3 mb-5">
                  <TextComponent htmlContent={generalDesc} />
                </div>
              )}

              {/* {anomalyData && "description" in anomalyData ? (
                  <div className="text-sm mt-3 mb-5">
                    {anomalyData && "description" in anomalyData ? (
                      anomalyData["description"]
                    ) : (
                      <div className="text-primary">
                        --Name description found--
                      </div>
                    )}
                  </div>
                ) : null} */}

              <div className="text-lg underline decoration-red-500 decoration-2 underline-offset-8">
                Anomaly Details
              </div>
              <div className="w-full grid grid-cols-4">
                <div className="col-span-3">
                  {incidentProps && Object.keys(incidentProps).length > 0 ? (
                    <ul className="text-sm mt-3 mb-5 space-y-2">
                      {"anomalous_hostname" in incidentProps &&
                      incidentProps.anomalous_hostname != "" ? (
                        <li>
                          <span className="font-bold">
                            Anomalous{" "}
                            {incidentProps?.anomaly_type == "entity"
                              ? "Host"
                              : "User"}
                            :
                          </span>{" "}
                          <span
                            onClick={() => {
                              if (incidentProps?.anomaly_type == "entity") {
                                setAssetDetailOpen(true);
                              } else if (
                                incidentProps?.anomaly_type == "user"
                              ) {
                                setUserDetailId(
                                  incidentProps["anomalous_hostname"],
                                );
                              }
                            }}
                            className="cursor-pointer font-bold"
                          >
                            {incidentProps["anomalous_hostname"]}
                          </span>
                          <a
                            href={shadow360Url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <PlayCircle
                              size={14}
                              className="inline-block ms-2 text-primary -mt-[3px] cursor-pointer"
                            />
                          </a>
                        </li>
                      ) : (
                        ""
                      )}
                      {"anomaly_type" in incidentProps &&
                      incidentProps.anomaly_type != "" ? (
                        <li>
                          <span className="font-bold">
                            Anomaly Type / Category:
                          </span>{" "}
                          {incidentProps["anomaly_type"]}
                        </li>
                      ) : (
                        ""
                      )}
                      {"Time" in incidentProps && incidentProps.Time != "" ? (
                        <li>
                          <span className="font-bold">Time:</span>{" "}
                          {isNaN(Number(incidentProps.Time))
                            ? incidentProps.Time
                            : EpochToDateTime(parseInt(incidentProps.Time))}
                        </li>
                      ) : (
                        ""
                      )}
                      <li>
                        <TextComponent
                          htmlContent={
                            incidentData &&
                            "<div class='font-bold inline-block me-1'>Description: </div>" +
                              incidentData.dynamic_desc
                          }
                        />
                      </li>
                      {"anomalous_ip" in incidentProps &&
                      incidentProps.anomalous_ip != "" &&
                      incidentProps.anomalous_ip ? (
                        <li>
                          <span className="font-bold">Local IP:</span>{" "}
                          {incidentProps["anomalous_ip"]}
                        </li>
                      ) : (
                        ""
                      )}
                      {"anomalous_ip_mac" in incidentProps &&
                      incidentProps["anomalous_ip_mac"] != "" &&
                      incidentProps["anomalous_ip_mac"] ? (
                        <li>
                          <span className="font-bold">Local MAC:</span>{" "}
                          {incidentProps["anomalous_ip_mac"]}
                        </li>
                      ) : (
                        ""
                      )}
                      {"globalID" in incidentProps &&
                      incidentProps["globalID"] &&
                      incidentProps["globalID"].toString() != "" ? (
                        <li>
                          <span className="font-bold">Global ID:</span> LSGUID_
                          {incidentProps["globalID"]}
                        </li>
                      ) : (
                        ""
                      )}
                      {"sensor" in incidentProps &&
                      incidentProps["sensor"] &&
                      incidentProps["sensor"].toString() != "" ? (
                        <li>
                          <span className="font-bold">Sensor Name : </span>{" "}
                          {incidentProps["sensor"]}
                        </li>
                      ) : (
                        ""
                      )}
                      {loggedUsers && loggedUsers.length > 0 ? (
                        <li>
                          <span className="font-bold">Last Login User :</span>{" "}
                          {loggedUsersSummary || "-"}
                          <button
                            type="button"
                            className="ms-2 text-primary underline text-xs"
                            onClick={() => setLoggedUsersModalOpen(true)}
                          >
                            View More
                          </button>
                        </li>
                      ) : (
                        ""
                      )}
                    </ul>
                  ) : (
                    ""
                  )}
                </div>
                <div className="col-span-1 relative min-h-60">
                  <div className="absolute w-20 h-20  top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4 bg-white p-2 rounded-full border-2">
                    <div
                      className="relative rounded-full shadow-lg text-center border-white h-full w-full bg-primary-background"
                      style={{
                        boxShadow: `inset 0px 0px 8px rgba(255,255,255,0.5)`,
                      }}
                    >
                      <div className="absolute left-2/4 top-2/4 -translate-x-2/4 -translate-y-2/4 font-bold text-2xl">
                        {incidentProps && "score" in incidentProps
                          ? incidentProps.score
                          : 0}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm absolute whitespace-nowrap left-2/4 -translate-x-2/4 top-2/4 mt-20 -translate-y-[80%]">
                    <div className="ms-[50%] mb-1">
                      <Triangle size={12} className="-translate-x-2/4" />
                    </div>
                    <div className="px-3 py-1 bg-secondary border-2 border-secondary shadow-lg text-primary-foreground rounded-sm inline-block">
                      Threat Score
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <div className="pe-16">
                <div className="text-lg underline decoration-red-500 decoration-2 underline-offset-8">
                  Description
                </div>
                {anomalyData && "description" in anomalyData ? (
                  <div className="text-sm mt-3 mb-5">
                    {anomalyData["description"]}
                  </div>
                ) : (
                  <div className="text-primary">--No description found--</div>
                )}

                {anomalyData &&
                "steps_to_verify" in anomalyData &&
                anomalyData.steps_to_verify &&
                anomalyData.steps_to_verify != "" ? (
                  <div>
                    <div className="text-lg underline decoration-red-500 decoration-2 underline-offset-8">
                      Steps to Investigate
                    </div>
                    <div className="text-sm mt-3 mb-5">
                      {anomalyData && "steps_to_verify" in anomalyData ? (
                        anomalyData.steps_to_verify
                          ?.replace(/\\n|\n/g, "")
                          .split("***")
                          .map((step: string, index: number) => {
                            if (step.trim() != "") {
                              return (
                                <li key={index} className="mb-1">
                                  {step}
                                </li>
                              );
                            }
                          })
                      ) : (
                        <div className="text-primary">--No data found--</div>
                      )}
                    </div>
                  </div>
                ) : null}

                {anomalyData &&
                "triggers" in anomalyData &&
                anomalyData.triggers &&
                anomalyData.triggers != "" ? (
                  <div>
                    <div className="text-lg underline decoration-red-500 decoration-2 underline-offset-8">
                      Potential Triggers
                    </div>
                    <div className="text-sm mt-3 mb-5">
                      {anomalyData && "triggers" in anomalyData ? (
                        anomalyData.triggers
                          ?.replace(/\n/g, "")
                          .split("***")
                          .map((step: string, index: number) => {
                            if (step.trim() != "") {
                              return (
                                <li key={index} className="mb-1">
                                  {step}
                                </li>
                              );
                            }
                          })
                      ) : (
                        <div className="text-primary">--No data found--</div>
                      )}
                    </div>
                  </div>
                ) : null}

                {anomalyData &&
                "recommended_solution" in anomalyData &&
                anomalyData.recommended_solution &&
                anomalyData.recommended_solution != "" ? (
                  <div>
                    <div className="text-lg underline decoration-red-500 decoration-2 underline-offset-8">
                      Recommended solution
                    </div>
                    <div className="text-sm mt-3 mb-5">
                      {anomalyData && "recommended_solution" in anomalyData ? (
                        anomalyData.recommended_solution
                          ?.replace(/\n/g, "")
                          .split("***")
                          .map((step: string, index: number) => {
                            if (step.trim() != "") {
                              return (
                                <li key={index} className="mb-1">
                                  {step}
                                </li>
                              );
                            }
                          })
                      ) : (
                        <div className="text-primary">--No data found--</div>
                      )}
                    </div>
                  </div>
                ) : null}

                {anomalyData &&
                "business_impact" in anomalyData &&
                anomalyData.business_impact &&
                anomalyData.business_impact != "" ? (
                  <div>
                    <div className="text-lg underline decoration-red-500 decoration-2 underline-offset-8">
                      Business Impact
                    </div>
                    <div className="text-sm mt-3 mb-5">
                      {anomalyData && "business_impact" in anomalyData ? (
                        anomalyData.business_impact
                          ?.replace(/\n/g, "")
                          .split("***")
                          .map((step: string, index: number) => {
                            if (step.trim() != "") {
                              return (
                                <li key={index} className="mb-1">
                                  {step}
                                </li>
                              );
                            }
                          })
                      ) : (
                        <div className="text-primary">--No data found--</div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {isFeatureAllowed(ALL_KEYS.AML_MITRE_ATTACK_DEFEND) &&
          mitre_attack &&
          mitre_attack.length > 0 ? (
            <div className="w-full mt-2">
              <div className="text-lg underline decoration-red-500 decoration-2 underline-offset-8">
                Mitre Attack
              </div>
              <div className="w-full grid gap-4 grid-cols-4 mt-3">
                {mitre_attack.map((mdata: any, ind: number) => (
                  <div
                    key={ind}
                    className="border rounded-md p-3 border-red-500"
                  >
                    <div className="text-sm text-red-500 mb-2">
                      {mdata["name"]}
                    </div>
                    {"mitigations" in mdata ? (
                      <ul className="text-sm list-disc list-inside">
                        {mdata["mitigations"].map(
                          (mitigation: any, index: number) => (
                            <li key={index}>{mitigation["name"]}</li>
                          ),
                        )}
                      </ul>
                    ) : (
                      ""
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            ""
          )}

          {isFeatureAllowed(ALL_KEYS.AML_MITRE_ATTACK_DEFEND) &&
          mitre_defend &&
          mitre_defend.length > 0 ? (
            <div className="w-full mt-5">
              <div className="text-lg underline decoration-red-500 decoration-2 underline-offset-8">
                MITRE Defend
              </div>
              <div className="w-full grid gap-4 grid-cols-4 mt-3">
                {mitre_defend.map((mdata: any, _index: number) => (
                  <div
                    key={_index}
                    className="col-span-1 border rounded-md p-3 border-green-600"
                  >
                    <div className="text-sm text-green-600 mb-2">
                      {mdata["d3fend_technique"]}
                    </div>
                    <div className="text-sm">{mdata["definition"]}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            ""
          )}
          {isFeatureAllowed(ALL_KEYS.AML_MITRE_ATTACK_DEFEND) &&
            (mitre_attack?.length || mitre_defend?.length) && <hr />}
          <div className="w-full mt-4">
            <div className="text-lg underline mb-4 decoration-red-500 decoration-2 underline-offset-8">
              Anomaly Log Details
            </div>
            <div className="grid w-full grid-cols-12 gap-2">
              <div className="col-span-3 text-sm min-h-[50vh] flex flex-col">
                <div className="grow">
                  <div className="w-full relative">
                    <SearchIcon
                      className="absolute left-2 top-[50%] -translate-y-[50%]"
                      size={14}
                    />
                    <Input
                      placeholder="Search..."
                      value={eventSearch}
                      onChange={(e) => searchEvent(e.target.value)}
                      size={12}
                      className="text-sm h-10 ps-8"
                    />
                  </div>
                  <hr className="py-1 mt-2" />
                  {incidentDetail &&
                    incidentDetail.map((data: any, index: number) => {
                      const evnames: any = {};
                      evnames[data.EventId] = data.EventName;
                      return (
                        <div
                          key={index}
                          className="bg-secondary rounded-md mb-2 cursor-pointer"
                          onClick={() => setEventTab(data.EventId)}
                        >
                          <div
                            className={
                              "p-2 border-2 rounded-md flex transition-all align-middle " +
                              (eventTab == data.EventId ? "border-primary" : "")
                            }
                          >
                            <EventDropdown
                              eventId={data.EventData.GlobalID}
                              anomalyType={incidentProps.anomaly_type}
                              anomalyTime={incidentProps.time_seen}
                              GlobalId={incidentProps.globalID}
                            />
                            <div className="ms-2">{data.EventName}</div>
                          </div>
                        </div>
                      );
                    })}
                </div>
                <div className="w-full grid grid-cols-12 gap-2 border-t pt-2 mt-2">
                  <div className="col-span-4">
                    <div className="mt-2 font-bold">
                      {incidentDetail && incidentDetail.length > 10
                        ? "10"
                        : incidentDetail.length}{" "}
                      of {incidentDetail.length} Events
                    </div>
                  </div>
                  <div className="col-span-8 text-right">
                    <div
                      className={
                        "inline-block p-2 rounded-md bg-secondary me-2 text-sm cursor-pointer shadow " +
                        (incidentDetail && incidentDetail.length > 10
                          ? "opacity-100"
                          : "opacity-40")
                      }
                    >
                      Previous
                    </div>
                    <div
                      className={
                        "inline-block p-2 rounded-md bg-secondary text-sm cursor-pointer shadow " +
                        (incidentDetail && incidentDetail.length > 10
                          ? "opacity-100"
                          : "opacity-40")
                      }
                    >
                      Next
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-span-9 border-l">
                {incidentDetail
                  ? incidentDetail.map((data: any, index: number) => {
                      const temp: any = [];

                      if (data.EventData) {
                        const finalLog = data.EventData;
                        let raw_message_obj =
                          finalLog?.data?.raw_message ||
                          finalLog?.Data?.raw_message;
                        if (!raw_message_obj || raw_message_obj.length == 0) {
                          raw_message_obj = [finalLog?.data || finalLog?.Data];
                        }
                        if (
                          raw_message_obj &&
                          typeof raw_message_obj == "string"
                        ) {
                          try {
                            const parsed = JSON.parse(raw_message_obj);
                            raw_message_obj = [parsed];
                          } catch (error) {
                            console.error(
                              "Failed to parse raw_message_obj:",
                              error,
                            );
                            raw_message_obj = [];
                          }
                        }
                        if (raw_message_obj) {
                          for (const loginfo of raw_message_obj) {
                            const display_logs =
                              loginfo?.["display"] || finalLog?.["display"];

                            temp.push({
                              Time:
                                data?.Time && isNaN(Number(data.Time))
                                  ? data.Time
                                  : EpochToDateTime(
                                      finalLog?.time_seen || finalLog.Time,
                                    ),
                              "Source IP": finalLog["sip"] || loginfo["sip"],
                              "Source Port":
                                finalLog["sport"] || loginfo["sport"],
                              "Destination IP":
                                finalLog["dip"] || loginfo["dip"],
                              "Destination Port":
                                finalLog["dport"] || loginfo["dport"],
                              "Destination MAC":
                                finalLog["dmac"] || loginfo["dmac"],
                              "Workstation IP": finalLog["IP"] || "- - -",
                              "Workstation Name":
                                finalLog["WorkstationName"] || "- - -",
                              Sensor:
                                "master" in loginfo
                                  ? loginfo["master"]
                                  : "- - -",
                              Version:
                                "version" in loginfo
                                  ? loginfo["version"]
                                  : "- - -",
                              "User ID": (
                                <div
                                  className="cursor-pointer"
                                  onClick={() =>
                                    setUserDetailId(
                                      loginfo["uid"] || finalLog["Username"],
                                    )
                                  }
                                >
                                  {loginfo["uid"] || finalLog["Username"]}
                                </div>
                              ),
                              log_source: finalLog["log_source"]
                                ? finalLog["log_source"].join(", ")
                                : loginfo["logType"],
                              fields: (
                                <ul className="list-disc">
                                  {Object.keys(display_logs).map(
                                    (key: string, index: number) => {
                                      return (
                                        <li
                                          key={index}
                                          className="break-all mb-2"
                                        >
                                          <span className="font-bold">
                                            {key}:{" "}
                                          </span>
                                          <span>{display_logs[key]}</span>
                                        </li>
                                      );
                                    },
                                  )}
                                </ul>
                              ),
                            });
                          }
                        }
                      }

                      return (
                        <div
                          className={
                            "w-full ps-2 -mt-4 " +
                            (eventTab == data.EventId ? "" : "hidden")
                          }
                          key={index}
                        >
                          <DataTable
                            data={temp}
                            cols={logcols}
                            visibleColumns={visibleCols}
                          />
                        </div>
                      );
                    })
                  : ""}
              </div>
            </div>
          </div>
          <div className="hidden top-0 right-4 z-10 -me-8">
            <div className="rounded-l-full shadow-xl bg-secondary inline-block border-l border-t border-b border-white translate-x-[1px] z-20">
              <img
                src={process.env.STATIC_PATH + "/img/shadowGPT/Circle.gif"}
                width={60}
                height={60}
                onClick={() => openShadowGPT()}
                className="cursor-pointer rounded-full me-2 shadowgpt-image"
                alt=""
              />
            </div>
            <div
              className={
                "w-[20vw] inline-block bg-secondary align-top h-[50vh] rounded-b-md rounded-tr-md border border-white transition-all " +
                (shadowOpen ? "me-6" : "-me-[20vw]")
              }
            >
              <ShadowGPT
                className="w-full h-full"
                data={{
                  anomalous_hostname: incidentProps.anomalous_hostname,
                  anomalous_ip: incidentProps.anomalous_ip,
                  anomalous_mac: incidentProps.anomalous_ip_mac,
                  anomaly_id: incidentProps.anomaly_id,
                  anomaly_type: incidentProps.anomaly_type,
                  anomaly_time: incidentProps.Time,
                  epoch_time: parseInt(incidentProps.inserted_time),
                  globalID: incidentProps.globalID,
                  score: "score" in incidentProps ? incidentProps.score : 0,
                  name: "name" in anomalyData ? anomalyData.name : "",
                  page: "incidentOverview",
                }}
              />
              <div className="relative h-full w-full hidden">
                <div>
                  <div className="text-sm font-bold" id="welcome"></div>
                </div>
                <div className="absolute bottom-0 left-0 w-full">
                  <div className="relative">
                    <Input placeholder="Ask ShadowGPT..." />
                    <MicIcon
                      size={26}
                      onClick={() => setRecording(!recording)}
                      className={
                        "absolute top-2/4 right-2 -translate-y-2/4 cursor-pointer hover:text-primary transition-all border rounded-full p-1 " +
                        (recording
                          ? "border-primary text-primary animate-pulse"
                          : "")
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={
            "p-2 rounded-b-md " + (mainTab == "shadow" ? "" : "hidden")
          }
        >
          <div className="w-full grid grid-cols-7 gap-2">
            <div className="col-span-1">
              <img
                src={process.env.STATIC_PATH + "/img/shadowGPT/Circle.gif"}
                style={{ mixBlendMode: "lighten" }}
                alt=""
                className="w-40 shadowgpt-image"
              />
            </div>
            <div className="col-span-6 relative h-full">
              <div className="text-lg font-bold absolute top-2/4 -translate-y-2/4">
                Welcome to ShadowGPT! How can I help you?
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const IncidentOverview: React.FC<IncidentOverviewProps> = ({
  openState,
  onClose,
  incidentProps,
}) => {
  const titleRef = React.useRef<HTMLSpanElement>(null);
  console.log("IncidentOverview incidentProps:", incidentProps);
  return ReactDOM.createPortal(
    <Dialog open={openState} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[80vw] px-4 h-[80vh] overflow-y-scroll gap-0 overflow-x-hidden content-baseline transform-none -mt-[40vh] -ms-[40vw]"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="mb-4">
            <span ref={titleRef}>Incident Overview</span>
          </DialogTitle>
        </DialogHeader>
        <IncidentOverviewComponent
          openState={openState}
          incidentProps={incidentProps}
          titleRef={titleRef}
        />
      </DialogContent>
    </Dialog>,
    document.body,
  );
};

export default IncidentOverview;

export { IncidentOverviewComponent };
